import {
  DnaIcon,
  FingerprintIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  LaptopIcon,
  MicroscopeIcon,
  ScanSearchIcon,
  SearchIcon,
  SkullIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Forensic Science Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to forensic science can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Foundations and evolution of forensic science',
      'Major forensic science disciplines',
      'Scientific reasoning in forensic investigation',
      'Evidence and its role in investigations',
      'Observation, documentation, and interpretation',
      'Reliability and limitations of forensic evidence',
      'Ethics and professional responsibility',
      'Bias and objectivity in forensic analysis',
      'Communicating scientific findings',
      'Forensic science in modern investigations',
    ],
    result:
      'Your students understand forensic science as a practical discipline that connects investigative science, critical thinking, and justice — not an impossible list of terms to memorize.',
    icon: MicroscopeIcon,
  },
  {
    number: '2',
    title: 'Crime Scene Investigation & Evidence',
    subtitle: 'Everything your students need to know about evidence recognition and investigation',
    context:
      'This is where many students struggle to build the investigative foundation. This volume gives you the materials to help them truly understand crime scene work.',
    items: [
      'Fundamentals of crime scene investigation',
      'Types and categories of evidence',
      'Scene observation and documentation',
      'Evidence recognition',
      'Evidence integrity',
      'Collection and preservation principles',
      'Chain of custody',
      'Contamination and evidence quality',
      'Scene interpretation',
      'Responsible conclusions and limitations',
      '12 practical crime scene case studies',
    ],
    result: 'Your students learn the investigative principles that underpin all forensic science work.',
    icon: SearchIcon,
  },
  {
    number: '3',
    title: 'Fingerprints, Impressions & Trace Evidence',
    subtitle: 'How forensic scientists compare and interpret physical evidence',
    context:
      'Fingerprint and trace evidence analysis is fascinating but complex. This volume gives you everything needed so your students grasp these evidence types clearly.',
    items: [
      'Fingerprint fundamentals',
      'Fingerprint patterns and characteristics',
      'Principles of fingerprint comparison',
      'Impression evidence',
      'Footwear and tire impressions',
      'Trace evidence fundamentals',
      'Hair and fiber evidence',
      'Glass, soil, and other trace materials',
      'Association versus identification',
      'Limitations and responsible interpretation',
      '15 worked fingerprint and trace evidence examples',
    ],
    result:
      'Your students understand how physical evidence is compared and what conclusions can responsibly be drawn.',
    icon: FingerprintIcon,
  },
  {
    number: '4',
    title: 'Forensic Biology & DNA Evidence',
    subtitle: 'DNA analysis and biological evidence from an educational foundation',
    context:
      'Forensic DNA is powerful but often misunderstood. This volume makes the science clear and comprehensible.',
    items: [
      'Fundamentals of forensic biology',
      'Biological evidence',
      'DNA structure and inheritance',
      'DNA evidence fundamentals',
      'DNA profiles',
      'Comparison and interpretation',
      'Reference and questioned samples',
      'Probability and evidentiary significance',
      'Contamination and limitations',
      'Ethics and responsible DNA interpretation',
      '15 practical DNA evidence case studies',
    ],
    result: 'Your students understand how DNA evidence works and what it can and cannot tell investigators.',
    icon: DnaIcon,
  },
  {
    number: '5',
    title: 'Forensic Chemistry & Toxicology',
    subtitle: 'How chemistry contributes to forensic analysis and investigation',
    context: 'Chemical and toxicological evidence is important but technical. This volume makes it accessible.',
    items: [
      'Foundations of forensic chemistry',
      'Chemical evidence',
      'Analytical reasoning',
      'Substance identification concepts',
      'Forensic toxicology fundamentals',
      'Toxic substances and their effects',
      'Toxicological evidence interpretation',
      'Screening and confirmation concepts',
      'Reliability and uncertainty',
      'Communicating chemical and toxicological findings',
      '12 practical chemistry and toxicology case studies',
    ],
    result:
      'Your students understand how chemical evidence is analyzed and how toxicological findings inform investigations.',
    icon: FlaskConicalIcon,
  },
  {
    number: '6',
    title: 'Forensic Pathology & Anthropology',
    subtitle: 'Forensic pathology and anthropology from an educational perspective',
    context:
      'Pathology and anthropology are important but sensitive. This volume teaches them responsibly and clearly.',
    items: [
      'Fundamentals of forensic pathology',
      'Role of the forensic pathologist',
      'Cause, mechanism, and manner of death',
      'Postmortem changes',
      'Estimating time-related evidence',
      'Fundamentals of forensic anthropology',
      'Human skeletal biology',
      'Biological profile concepts',
      'Evidence interpretation and limitations',
      'Ethics and professional responsibility',
      '12 practical pathology and anthropology case studies',
    ],
    result:
      'Your students understand how forensic specialists contribute to death investigations and victim identification.',
    icon: SkullIcon,
  },
  {
    number: '7',
    title: 'Digital Forensics & Cyber Evidence',
    subtitle: 'Digital evidence in modern forensic investigations',
    context:
      'Digital forensics is rapidly evolving and important to understand. This volume covers it comprehensively.',
    items: [
      'Digital forensics fundamentals',
      'Types of digital evidence',
      'Computers and mobile devices as evidence sources',
      'Files, metadata, and digital records',
      'Online and communication evidence',
      'Digital timelines',
      'Preservation and integrity',
      'Authentication concepts',
      'Privacy and ethical considerations',
      'Limitations of digital evidence and responsible interpretation',
      '15 practical digital forensics case studies',
    ],
    result:
      'Your students understand how digital evidence is collected, preserved, and analyzed in modern investigations.',
    icon: LaptopIcon,
  },
  {
    number: '8',
    title: 'Forensic Evidence Analysis & Case Reconstruction',
    subtitle: 'Bringing multiple forensic disciplines together',
    context: 'This is the integrative volume. Your students learn how evidence sources work together.',
    items: [
      'Evidence integration',
      'Relationships between evidence sources',
      'Building investigative timelines',
      'Event sequencing',
      'Case reconstruction principles',
      'Competing hypotheses',
      'Corroborating and conflicting evidence',
      'Missing and incomplete information',
      'Bias and alternative explanations',
      'Evidence-based conclusions',
      'Uncertainty and limitations',
      'Communicating reconstruction findings',
      '15 integrated case reconstruction examples',
    ],
    result:
      'Your students understand how forensic specialists evaluate multiple evidence sources collectively and develop evidence-based conclusions.',
    icon: ScanSearchIcon,
  },
  {
    number: '9',
    title: 'Teaching Forensic Science: Curriculum, Activities & Classroom Practice',
    subtitle: 'Your complete guide as a forensic science education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of forensic science teaching.',
    items: [
      'Planning a Forensic Science curriculum',
      'Establishing learning objectives',
      'Organizing units and lessons',
      'Classroom teaching strategies',
      'Inquiry-based learning',
      'Case-based learning',
      'Classroom activities',
      'Critical-thinking development',
      'Student discussion strategies',
      'Assessment approaches',
      'Teaching controversial or sensitive topics responsibly',
      'Creating an engaging and academically rigorous classroom',
      '10 complete integrated forensic science projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
